import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ProjectCard } from "@/components/ProjectCard";
import { AREAS, areaBySlug } from "@/lib/projectMeta";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsListPage({ area = null }) {
  const [projects, setProjects] = useState(null);
  const activeArea = area ? areaBySlug(area) : null;

  useEffect(() => {
    const url = area ? `${API}/api/projects?area=${area}` : `${API}/api/projects`;
    fetch(url).then((r) => r.json()).then(setProjects).catch(() => setProjects([]));
  }, [area]);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-[#1f2d22] text-white py-20 sm:py-28 overflow-hidden" data-testid="progetti-hero">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#2E4F3B]/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              {activeArea ? activeArea.name : "La piattaforma dei progetti"}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.03] tracking-tight max-w-4xl">
            {activeArea ? activeArea.name : <>I progetti di <span className="italic font-light text-[#E8C9A0]">Per Asciano.</span></>}
          </h1>
          {activeArea ? (
            <p className="mt-6 text-base sm:text-xl text-white/80 max-w-3xl leading-relaxed">{activeArea.desc}</p>
          ) : (
            <>
              <p className="mt-6 text-base sm:text-xl text-white/80 max-w-3xl leading-relaxed">
                Cinque aree di intervento, un unico obiettivo: costruire un territorio più curato, sicuro, vivo e capace di guardare al futuro.
              </p>
              <p className="mt-5 text-sm sm:text-base text-white/65 max-w-3xl leading-relaxed">
                Per Asciano nasce dall'esigenza di trasformare la partecipazione dei cittadini in progetti concreti. Alcuni progetti sono già iniziati,
                altri sono in fase di progettazione e altri ancora cercano partner, competenze e risorse per diventare realtà. Questa pagina è uno
                spazio aperto: scopri i progetti, sostienili e aiutaci a migliorarli.
              </p>
            </>
          )}
          {activeArea && (
            <Link to="/progetti" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#E8C9A0] hover:gap-3 transition-all">
              ← Tutte le aree
            </Link>
          )}
        </div>
      </section>

      {/* AREAS NAV (only on main page) */}
      {!area && (
        <section className="py-14 sm:py-20 bg-white" data-testid="areas-nav">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <h2 className="font-display text-2xl sm:text-3xl font-black text-[#1A1A1A] mb-8">Le 5 aree di intervento</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {AREAS.map((a) => {
                const Icon = a.icon;
                return (
                  <Link key={a.slug} to={`/progetti/${a.slug}`} data-testid={`area-card-${a.slug}`}
                    className="group rounded-2xl border-2 border-[#E5E1D8] p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                    style={{ borderTopColor: a.color, borderTopWidth: 4 }}>
                    <span className="inline-flex w-11 h-11 rounded-xl items-center justify-center text-white mb-3" style={{ backgroundColor: a.color }}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="font-display font-black text-[#1A1A1A] leading-tight">{a.name}</div>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#2E4F3B] group-hover:gap-2 transition-all">
                      Vedi progetti <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROJECTS */}
      <section className="py-14 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {projects === null ? (
            <div className="flex items-center justify-center py-16 text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento…</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessun progetto in questa area.</div>
          ) : area ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
          ) : (
            AREAS.map((a) => {
              const list = projects.filter((p) => p.area === a.slug);
              if (!list.length) return null;
              const Icon = a.icon;
              return (
                <div key={a.slug} className="mb-14 last:mb-0" id={a.slug}>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="inline-flex w-9 h-9 rounded-lg items-center justify-center text-white" style={{ backgroundColor: a.color }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <h2 className="font-display text-2xl sm:text-3xl font-black text-[#1A1A1A]">{a.name}</h2>
                    <Link to={`/progetti/${a.slug}`} className="ml-auto text-xs font-bold text-[#2E4F3B] hover:underline">Approfondisci →</Link>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((p) => <ProjectCard key={p.id} project={p} />)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </Layout>
  );
}
